import { useState, useEffect, useCallback, useRef } from "react";
import { useSearchParams } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Phone, KeyRound, Car, User, Loader2, CheckCircle,
  Clock, ArrowRight, Shield, MapPin, RefreshCw, AlertCircle
} from "lucide-react";
import { toast } from "sonner";
import axios from "axios";
import ValetJourneyAnimation from "@/components/ValetJourneyAnimation";
import PushPrompt from "@/components/PushPrompt";
import { usePushNotifications } from "@/hooks/usePushNotifications";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const STATUS_CONFIG = {
  parked: { label: "Parked", color: "text-blue-400", bg: "bg-blue-500/10", icon: MapPin, description: "Your car is safely parked. Tap below when you're ready to leave." },
  requested: { label: "Requested", color: "text-amber-400", bg: "bg-amber-500/10", icon: Clock, description: "Your car has been requested. The valet team is on it!" },
  fetching: { label: "Being Fetched", color: "text-orange-400", bg: "bg-orange-500/10", icon: Car, description: "A valet is bringing your car to the exit right now." },
  ready: { label: "Ready!", color: "text-emerald-400", bg: "bg-emerald-500/10", icon: CheckCircle, description: "Your car is waiting at the exit. Please proceed!" },
  completed: { label: "Complete", color: "text-gray-400", bg: "bg-gray-500/10", icon: CheckCircle, description: "Your visit is complete. Thank you for using VeloPark!" },
};

// Step 1: Phone Input
const PhoneStep = ({ onSubmit, loading, venueName }) => {
  const [phone, setPhone] = useState("");

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!phone || phone.length < 7) {
      toast.error("Please enter a valid phone number");
      return;
    }
    onSubmit(phone);
  };

  return (
    <div className="space-y-6" data-testid="guest-phone-step">
      <div className="text-center space-y-2">
        <div className="w-16 h-16 mx-auto rounded-2xl bg-[#D4AF37]/10 flex items-center justify-center">
          <Phone className="w-8 h-8 text-[#D4AF37]" />
        </div>
        <h2 className="text-2xl font-bold text-white">Welcome to VeloPark</h2>
        {venueName && <p className="text-[#D4AF37] font-medium">{venueName}</p>}
        <p className="text-gray-400 text-sm">Enter your phone number to get started</p>
      </div>

      <form onSubmit={handleSubmit} className="space-y-4">
        <div className="space-y-2">
          <Label className="text-gray-300 text-sm">Phone Number</Label>
          <div className="relative">
            <Phone className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-500" />
            <Input
              data-testid="guest-phone-input"
              type="tel"
              placeholder="+1 234 567 8900"
              value={phone}
              onChange={(e) => setPhone(e.target.value)}
              className="pl-10 bg-white/5 border-white/10 text-white placeholder:text-gray-500 h-12 rounded-xl focus:border-[#D4AF37]/50 focus:ring-[#D4AF37]/20"
            />
          </div>
        </div>
        <Button
          data-testid="guest-send-otp-btn"
          type="submit"
          disabled={loading}
          className="w-full h-12 rounded-xl bg-[#D4AF37] hover:bg-[#C4A030] text-black font-semibold"
        >
          {loading ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <ArrowRight className="w-4 h-4 mr-2" />}
          {loading ? "Sending..." : "Send Verification Code"}
        </Button>
      </form>
    </div>
  );
};

// Step 2: OTP Verification
const OtpStep = ({ phone, mockOtp, onVerify, onResend, loading }) => {
  const [otp, setOtp] = useState("");

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!otp || otp.length < 6) {
      toast.error("Please enter the 6-digit code");
      return;
    }
    onVerify(otp);
  };

  return (
    <div className="space-y-6" data-testid="guest-otp-step">
      <div className="text-center space-y-2">
        <div className="w-16 h-16 mx-auto rounded-2xl bg-[#D4AF37]/10 flex items-center justify-center">
          <KeyRound className="w-8 h-8 text-[#D4AF37]" />
        </div>
        <h2 className="text-2xl font-bold text-white">Verify Your Phone</h2>
        <p className="text-gray-400 text-sm">We sent a code to <span className="text-white">{phone}</span></p>
      </div>

      {mockOtp && (
        <div className="bg-amber-500/10 border border-amber-500/20 rounded-xl p-3 text-center" data-testid="guest-mock-otp-display">
          <p className="text-amber-300 text-xs mb-1">Demo Mode - Your verification code:</p>
          <p className="text-amber-400 text-2xl font-mono font-bold tracking-[0.3em]">{mockOtp}</p>
        </div>
      )}

      <form onSubmit={handleSubmit} className="space-y-4">
        <div className="space-y-2">
          <Label className="text-gray-300 text-sm">Verification Code</Label>
          <div className="relative">
            <KeyRound className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-500" />
            <Input
              data-testid="guest-otp-input"
              type="text"
              inputMode="numeric"
              maxLength={6}
              placeholder="000000"
              value={otp}
              onChange={(e) => setOtp(e.target.value.replace(/\D/g, ""))}
              className="pl-10 bg-white/5 border-white/10 text-white placeholder:text-gray-500 h-12 rounded-xl text-center text-xl tracking-[0.3em] font-mono focus:border-[#D4AF37]/50 focus:ring-[#D4AF37]/20"
            />
          </div>
        </div>
        <Button
          data-testid="guest-verify-otp-btn"
          type="submit"
          disabled={loading}
          className="w-full h-12 rounded-xl bg-[#D4AF37] hover:bg-[#C4A030] text-black font-semibold"
        >
          {loading ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <CheckCircle className="w-4 h-4 mr-2" />}
          {loading ? "Verifying..." : "Verify Code"}
        </Button>
        <button
          type="button"
          onClick={onResend}
          className="w-full text-sm text-gray-400 hover:text-[#D4AF37] transition-colors"
          data-testid="guest-resend-otp-btn"
        >
          Didn't get the code? Resend
        </button>
      </form>
    </div>
  );
};

// Step 3: Car Details
const DetailsStep = ({ onSubmit, loading, venueName }) => {
  const [name, setName] = useState("");
  const [carDetails, setCarDetails] = useState("");
  const [tableNum, setTableNum] = useState("");

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!name.trim()) {
      toast.error("Please enter your name");
      return;
    }
    if (!carDetails.trim()) {
      toast.error("Please enter your car details");
      return;
    }
    onSubmit(name.trim(), carDetails.trim(), tableNum.trim() || null);
  };

  return (
    <div className="space-y-6" data-testid="guest-details-step">
      <div className="text-center space-y-2">
        <div className="w-16 h-16 mx-auto rounded-2xl bg-[#D4AF37]/10 flex items-center justify-center">
          <Car className="w-8 h-8 text-[#D4AF37]" />
        </div>
        <h2 className="text-2xl font-bold text-white">Your Details</h2>
        {venueName && <p className="text-[#D4AF37] font-medium text-sm">{venueName}</p>}
        <p className="text-gray-400 text-sm">Tell us about you and your car</p>
      </div>

      <form onSubmit={handleSubmit} className="space-y-4">
        <div className="space-y-2">
          <Label className="text-gray-300 text-sm">Your Name</Label>
          <div className="relative">
            <User className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-500" />
            <Input
              data-testid="guest-name-input"
              type="text"
              placeholder="John Doe"
              value={name}
              onChange={(e) => setName(e.target.value)}
              className="pl-10 bg-white/5 border-white/10 text-white placeholder:text-gray-500 h-12 rounded-xl focus:border-[#D4AF37]/50 focus:ring-[#D4AF37]/20"
            />
          </div>
        </div>
        <div className="space-y-2">
          <Label className="text-gray-300 text-sm">Car Details</Label>
          <div className="relative">
            <Car className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-500" />
            <Input
              data-testid="guest-car-input"
              type="text"
              placeholder="Red Toyota Camry - ABC 1234"
              value={carDetails}
              onChange={(e) => setCarDetails(e.target.value)}
              className="pl-10 bg-white/5 border-white/10 text-white placeholder:text-gray-500 h-12 rounded-xl focus:border-[#D4AF37]/50 focus:ring-[#D4AF37]/20"
            />
          </div>
          <p className="text-gray-500 text-xs">Color, make, model, and plate number</p>
        </div>
        <div className="space-y-2">
          <Label className="text-gray-300 text-sm">Table Number <span className="text-gray-600">(optional)</span></Label>
          <div className="relative">
            <MapPin className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-500" />
            <Input
              data-testid="guest-table-input"
              type="text"
              placeholder="T12, VIP-3, Patio-5..."
              value={tableNum}
              onChange={(e) => setTableNum(e.target.value)}
              className="pl-10 bg-white/5 border-white/10 text-white placeholder:text-gray-500 h-12 rounded-xl focus:border-[#D4AF37]/50 focus:ring-[#D4AF37]/20"
            />
          </div>
        </div>
        <Button
          data-testid="guest-register-btn"
          type="submit"
          disabled={loading}
          className="w-full h-12 rounded-xl bg-[#D4AF37] hover:bg-[#C4A030] text-black font-semibold"
        >
          {loading ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <CheckCircle className="w-4 h-4 mr-2" />}
          {loading ? "Registering..." : "Register My Car"}
        </Button>
      </form>
    </div>
  );
};

// Step 4: Status Tracking
const StatusStep = ({ guestData, guestToken, onRefresh, refreshing }) => {
  const [requesting, setRequesting] = useState(false);
  const statusInfo = STATUS_CONFIG[guestData.status] || STATUS_CONFIG.parked;
  const pushNotif = usePushNotifications({ guestId: guestData.guest_id });
  const StatusIcon = statusInfo.icon;

  const handleRequestCar = async () => {
    setRequesting(true);
    try {
      await axios.post(`${API}/guest/${guestData.guest_id}/request-car`, {}, {
        headers: { Authorization: `Bearer ${guestToken}` }
      });
      toast.success("Car requested! The valet team is on it.");
      onRefresh();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to request car");
    } finally {
      setRequesting(false);
    }
  };

  return (
    <div className="space-y-5" data-testid="guest-status-step">
      {/* Valet Journey Animation */}
      <ValetJourneyAnimation status={guestData.status} />

      {/* Push notification prompt */}
      <PushPrompt
        onSubscribe={pushNotif.subscribe}
        isSubscribed={pushNotif.isSubscribed}
        supported={pushNotif.supported}
        permission={pushNotif.permission}
      />

      <div className="text-center space-y-1">
        <h2 className="text-xl font-bold text-white">{guestData.guest_name}</h2>
        <p className="text-gray-400 text-sm">{guestData.venue_name}</p>
      </div>

      {/* Status Card */}
      <div className="bg-white/5 border border-white/10 rounded-2xl p-5 space-y-4">
        <div className="flex items-center justify-between">
          <span className="text-gray-400 text-sm">Status</span>
          <span className={`${statusInfo.color} font-semibold text-lg`} data-testid="guest-status-label">{statusInfo.label}</span>
        </div>
        <p className="text-gray-300 text-sm">{statusInfo.description}</p>

        {guestData.estimated_wait && (
          <div className="bg-[#D4AF37]/10 border border-[#D4AF37]/20 rounded-xl p-3 flex items-center gap-3">
            <Clock className="w-5 h-5 text-[#D4AF37] shrink-0" />
            <div>
              <p className="text-white text-sm font-medium">
                Est. wait: {guestData.estimated_wait.estimated_minutes} min
              </p>
              {guestData.estimated_wait.zone && (
                <p className="text-gray-400 text-xs">Zone: {guestData.estimated_wait.zone}</p>
              )}
            </div>
          </div>
        )}

        <div className="grid grid-cols-2 gap-3 text-sm">
          <div className="bg-white/5 rounded-xl p-3">
            <p className="text-gray-500 text-xs mb-1">Car</p>
            <p className="text-white font-medium truncate" data-testid="guest-car-details">{guestData.car_details}</p>
          </div>
          <div className="bg-white/5 rounded-xl p-3">
            <p className="text-gray-500 text-xs mb-1">Token</p>
            <p className="text-[#D4AF37] font-mono font-bold" data-testid="guest-parking-token">{guestData.parking_token}</p>
          </div>
          {guestData.table_number && (
            <div className="bg-white/5 rounded-xl p-3 col-span-2">
              <p className="text-gray-500 text-xs mb-1">Table</p>
              <p className="text-purple-400 font-medium" data-testid="guest-table-number">{guestData.table_number}</p>
            </div>
          )}
        </div>
      </div>

      {/* Get My Car Button */}
      {guestData.status === "parked" && (
        <Button
          data-testid="guest-get-car-btn"
          onClick={handleRequestCar}
          disabled={requesting}
          className="w-full h-14 rounded-xl bg-[#D4AF37] hover:bg-[#C4A030] text-black font-bold text-lg shadow-lg shadow-[#D4AF37]/20"
        >
          {requesting ? (
            <Loader2 className="w-5 h-5 animate-spin mr-2" />
          ) : (
            <Car className="w-5 h-5 mr-2" />
          )}
          {requesting ? "Requesting..." : "Get My Car"}
        </Button>
      )}

      {guestData.status === "ready" && (
        <div className="bg-emerald-500/10 border border-emerald-500/30 rounded-2xl p-5 text-center space-y-2">
          <CheckCircle className="w-10 h-10 text-emerald-400 mx-auto" />
          <p className="text-emerald-300 font-semibold text-lg">Your car is at the exit!</p>
          <p className="text-gray-400 text-sm">Please head to the pickup area</p>
        </div>
      )}

      {/* Refresh Button */}
      <button
        onClick={onRefresh}
        disabled={refreshing}
        className="w-full flex items-center justify-center gap-2 text-gray-400 hover:text-[#D4AF37] text-sm transition-colors py-2"
        data-testid="guest-refresh-btn"
      >
        <RefreshCw className={`w-4 h-4 ${refreshing ? "animate-spin" : ""}`} />
        {refreshing ? "Refreshing..." : "Refresh Status"}
      </button>
    </div>
  );
};

// Main Guest Page
export default function GuestPage() {
  const [searchParams] = useSearchParams();
  const venueId = searchParams.get("venue");

  const [step, setStep] = useState("phone"); // phone, otp, details, status
  const [phone, setPhone] = useState("");
  const [mockOtp, setMockOtp] = useState(null);
  const [venueName, setVenueName] = useState("");
  const [guestToken, setGuestToken] = useState("");
  const [guestId, setGuestId] = useState("");
  const [guestData, setGuestData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [refreshing, setRefreshing] = useState(false);
  const [error, setError] = useState(null);
  const pollRef = useRef(null);

  // Restore session from localStorage
  useEffect(() => {
    const savedSession = localStorage.getItem(`velopark_guest_${venueId}`);
    if (savedSession) {
      try {
        const session = JSON.parse(savedSession);
        if (session.guestToken && session.guestId) {
          setGuestToken(session.guestToken);
          setGuestId(session.guestId);
          setStep("status");
          fetchStatus(session.guestId, session.guestToken);
        }
      } catch (e) {
        localStorage.removeItem(`velopark_guest_${venueId}`);
      }
    }
  }, [venueId]);

  // Auto-poll status every 10 seconds when in status step
  useEffect(() => {
    if (step === "status" && guestId && guestToken) {
      pollRef.current = setInterval(() => {
        fetchStatus(guestId, guestToken, true);
      }, 10000);
      return () => clearInterval(pollRef.current);
    }
  }, [step, guestId, guestToken]);

  const fetchStatus = useCallback(async (id, token, silent = false) => {
    if (!silent) setRefreshing(true);
    try {
      const res = await axios.get(`${API}/guest/status/${id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setGuestData(res.data);
      setVenueName(res.data.venue_name);
      // If completed, clear session
      if (res.data.status === "completed") {
        localStorage.removeItem(`velopark_guest_${venueId}`);
        clearInterval(pollRef.current);
      }
    } catch (err) {
      if (err.response?.status === 401) {
        // Token expired, reset
        localStorage.removeItem(`velopark_guest_${venueId}`);
        setStep("phone");
        setGuestToken("");
        setGuestId("");
        if (!silent) toast.error("Session expired. Please verify again.");
      }
    } finally {
      if (!silent) setRefreshing(false);
    }
  }, [venueId]);

  if (!venueId) {
    return (
      <div className="min-h-screen bg-[#090A0F] flex items-center justify-center p-4">
        <div className="text-center space-y-4">
          <AlertCircle className="w-12 h-12 text-red-400 mx-auto" />
          <h2 className="text-xl font-bold text-white">Invalid QR Code</h2>
          <p className="text-gray-400 text-sm">Please scan a valid VeloPark QR code at the venue.</p>
        </div>
      </div>
    );
  }

  const handleSendOtp = async (phoneNum) => {
    setLoading(true);
    setError(null);
    try {
      const res = await axios.post(`${API}/guest/send-otp`, { phone: phoneNum, venue_id: venueId });
      setPhone(phoneNum);
      setVenueName(res.data.venue_name || "");
      if (res.data.mock_otp) setMockOtp(res.data.mock_otp);
      setStep("otp");
      toast.success("Verification code sent!");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to send code");
    } finally {
      setLoading(false);
    }
  };

  const handleVerifyOtp = async (otp) => {
    setLoading(true);
    try {
      const res = await axios.post(`${API}/guest/verify-otp`, { phone, otp, venue_id: venueId });
      setGuestToken(res.data.guest_token);

      if (res.data.already_registered) {
        // Returning guest
        setGuestId(res.data.guest_id);
        localStorage.setItem(`velopark_guest_${venueId}`, JSON.stringify({
          guestToken: res.data.guest_token,
          guestId: res.data.guest_id
        }));
        const returnVenueName = res.data.venue_name || venueName;
        setVenueName(returnVenueName);
        setGuestData({
          guest_id: res.data.guest_id,
          guest_name: res.data.guest_name,
          car_details: res.data.car_details,
          venue_name: returnVenueName,
          status: res.data.status,
          parking_token: res.data.parking_token || ""
        });
        setStep("status");
        fetchStatus(res.data.guest_id, res.data.guest_token);
        toast.success("Welcome back!");
      } else {
        setStep("details");
        toast.success("Phone verified!");
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || "Verification failed");
    } finally {
      setLoading(false);
    }
  };

  const handleResendOtp = () => {
    setMockOtp(null);
    handleSendOtp(phone);
  };

  const handleRegister = async (name, carDetails, tableNum) => {
    setLoading(true);
    try {
      const res = await axios.post(`${API}/guest/register`, {
        guest_name: name,
        car_details: carDetails,
        table_number: tableNum
      }, {
        headers: { Authorization: `Bearer ${guestToken}` }
      });

      const newToken = res.data.guest_token;
      const newGuestId = res.data.guest_id;

      setGuestToken(newToken);
      setGuestId(newGuestId);
      setVenueName(res.data.venue_name);

      localStorage.setItem(`velopark_guest_${venueId}`, JSON.stringify({
        guestToken: newToken,
        guestId: newGuestId
      }));

      setGuestData({
        guest_id: newGuestId,
        guest_name: name,
        car_details: carDetails,
        venue_name: res.data.venue_name,
        parking_token: res.data.parking_token,
        table_number: tableNum || null,
        status: res.data.status,
        estimated_wait: null
      });

      setStep("status");
      toast.success("Car registered! You're all set.");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Registration failed");
    } finally {
      setLoading(false);
    }
  };

  const handleRefresh = () => {
    if (guestId && guestToken) {
      fetchStatus(guestId, guestToken);
    }
  };

  return (
    <div className="min-h-screen bg-[#090A0F] flex flex-col" data-testid="guest-page">
      {/* Header */}
      <div className="px-4 py-4 border-b border-white/5">
        <div className="flex items-center justify-center gap-2">
          <img src="/logo.svg" alt="VeloPark" className="w-5 h-5 rounded-full" />
          <span className="text-white font-bold text-lg tracking-wide">VeloPark</span>
        </div>
      </div>

      {/* Progress bar */}
      <div className="px-6 pt-4">
        <div className="flex gap-1.5">
          {["phone", "otp", "details", "status"].map((s, i) => {
            const stepOrder = { phone: 0, otp: 1, details: 2, status: 3 };
            const currentOrder = stepOrder[step];
            const isActive = i <= currentOrder;
            return (
              <div
                key={s}
                className={`h-1 flex-1 rounded-full transition-all duration-500 ${isActive ? "bg-[#D4AF37]" : "bg-white/10"}`}
              />
            );
          })}
        </div>
      </div>

      {/* Content */}
      <div className="flex-1 flex items-center justify-center p-6">
        <div className="w-full max-w-sm">
          {step === "phone" && (
            <PhoneStep onSubmit={handleSendOtp} loading={loading} venueName={venueName} />
          )}
          {step === "otp" && (
            <OtpStep
              phone={phone}
              mockOtp={mockOtp}
              onVerify={handleVerifyOtp}
              onResend={handleResendOtp}
              loading={loading}
            />
          )}
          {step === "details" && (
            <DetailsStep onSubmit={handleRegister} loading={loading} venueName={venueName} />
          )}
          {step === "status" && guestData && (
            <StatusStep
              guestData={guestData}
              guestToken={guestToken}
              onRefresh={handleRefresh}
              refreshing={refreshing}
            />
          )}
        </div>
      </div>

      {/* Footer */}
      <div className="px-4 py-3 border-t border-white/5 text-center">
        <p className="text-gray-500 text-xs flex items-center justify-center gap-1">
          <Shield className="w-3 h-3" />
          Secured by VeloPark. Your data is private.
        </p>
        <a href="https://velopark.in" target="_blank" rel="noopener noreferrer" className="text-gray-600 text-[10px] hover:text-[#D4AF37] transition-colors">velopark.in</a>
      </div>
    </div>
  );
}
